import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { StationsDropDown } from '@/Interface/analysisEmi';
import { MonthlyReport } from '@/Interface/monthlyReport';
import { RegionAndCity } from '@/Interface/regionAndCity';
import { TariffTypeAndCompany } from '@/Interface/tariffs';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PermissionsService } from '@services/permissions.service';
import { UtilityService } from '@services/utility.service';
import * as Highcharts from 'highcharts';
import moment from 'moment';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { ToastrService } from 'ngx-toastr';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-monthly-report',
  templateUrl: './monthly-report.component.html',
  styleUrls: ['./monthly-report.component.scss']
})
export class MonthlyReportComponent implements OnInit {
  public pageSizeControl = new FormControl(10);
  pageSize: number;
  pageToGo: number = 1;
  searchElement = '';
  serialNumberArray: Array<number> = [];
  next_page: number;
  current_page: number;
  previous_page: number;
  total_pages: number;
  total_items: number;
  private latestQueryParams: any = {};
  sortColumn: string = null;
  sortOrder: 'asc' | 'desc' = null;
  searchSubject: Subject<string> = new Subject();
  loader: boolean = false
  isLoadingTableInner: boolean = false

  public pageRecordOptions = [
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
    { value: '200', label: '200' },
  ];

  public citiesList: TariffTypeAndCompany[] = [];
  public selectedId: number
  allPlantList: StationsDropDown;
  selectedDate: Date = null;
  datePickerConfig: Partial<BsDaterangepickerConfig> = {
    showWeekNumbers: false,
    dateInputFormat: 'MMM, YYYY',
    minMode: 'month',
  };
  datePickerConfig2: Partial<BsDaterangepickerConfig> = {
    showWeekNumbers: false,
    dateInputFormat: 'DD/MM/YYYY',
  };
  plantDetails: MonthlyReport | null = null;
  modalRef: NgbModalRef | null = null;
  isDownloading: boolean;
  isSaving: boolean = false;
  downtimeList: { label: string, value: string }[] = [];
  form = {
    tax: '',
    lossOfEnergy: '',
    remarks: ''
  };
  today = new Date();
  Highcharts: typeof Highcharts = Highcharts;
  public billingChart!: Highcharts.Options;
  public roiChart!: Highcharts.Options;
  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady: boolean = false;

  ownedPlants: any[] = [];
  subscribedPlants: any[] = [];
  existingSubscription: any = null;
  isLoadingSubscription: boolean = false;
  currentUserId: number | null = null;
  subscribeForm: any;
  showUnsubscribeConfirm: boolean = false;

  frequencyOptions = [
    { label: 'Monthly', value: 'MONTHLY' },
    { label: 'Custom', value: 'CUSTOM' }
  ];
  fromDayOptions: { label: string, value: number }[] = [];
  toDayValue: number | null = null;
  availablePlants: any[] = [];

  // fromDate: Date | null = null;
  // toDate: Date | null = null;

  constructor(private modalService: NgbModal,
    private _utilityService: UtilityService,
    private toaster: ToastrService,
    private formBuilders: FormBuilder,
    private permService: PermissionsService,
  ) { }

  ngOnInit(): void {
    this.initComponent()
  }
  async initComponent() {
    this.loader = true
    await this.getComponentPermissions();

    if (this.permissions.canView) {
      this.getPlantsDropDown();
      const userDetail: any = this._utilityService.decodeUserCode();
      this.currentUserId = userDetail.user_id;
      this.initSubscribeForm();
      this.generateFromDayOptions()
      this.generateDowntimeOptions();
    }
    setTimeout(() => {
      this.loader = false;
    }, 250);
  }

  async getComponentPermissions() {
    const url = this.permService.getCurrentUrl();
    // const menuFromLocal = this.permService.loadMenuFromLocalStorage();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();

    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
        canAdd: !!matchedMenu.canAdd,
        canEdit: !!matchedMenu.canEdit,
        canView: !!matchedMenu.canView,
        canDelete: !!matchedMenu.canDelete
      }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }
  get hasAnyActionPermission(): boolean {
    return (
      this.permissionsReady &&
      (this.permissions.canView ||
        this.permissions.canEdit ||
        this.permissions.canDelete)
    );
  }

  private generateDowntimeOptions() {
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 5) { // increment by 5 minutes for better usability
        const hours = h.toString().padStart(2, '0');
        const minutes = m.toString().padStart(2, '0');
        this.downtimeList.push({
          label: `${hours}h ${minutes}m`,
          value: `${hours}:${minutes}`
        });
      }
    }
  }
  resetForm() {
    // this.dailyReportForm.reset();
  }
  billingChartInit(): void {
    const items = this.plantDetails?.billing_period?.items || [];

    const categories = items.map(item => {
      const dateObj = new Date(item.date);
      const day = dateObj.getDate();
      const monthShort = dateObj.toLocaleString('default', { month: 'short' });
      return `${day}-${monthShort}`; // e.g., 14-Aug
    });

    const data = items.map(item => item.pvyield);

    // ✅ Check if all data is 0 or null/undefined
    const allDataEmpty = data.length === 0 || data.every(val => !val || val === 0);

    if (categories.length === 0 || allDataEmpty) {
      this.billingChart = null;
      return;
    }
    // ✅ If data is valid, build chart
    this.billingChart = {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        height: 467,
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: categories,
        crosshair: true,
        labels: {
          rotation: 0,
          autoRotation: undefined,
          style: {
            color: '#000',
            fontSize: '12px',
            fontWeight: '400'
          }
        }
      },
      yAxis: {
        title: {
          text: null
        },
        labels: {
          style: {
            color: '#000',
            fontSize: '12px',
            fontWeight: '400'
          }
        }
      },
      tooltip: {
        valueSuffix: ' kWh'
      },
      legend: {
        enabled: false
      },
      plotOptions: {
        column: {
          borderRadius: 2,
          pointWidth: 36,
          color: '#FB8C04'
        }
      },
      series: [
        {
          name: 'PV Yield',
          type: 'column',
          data: data
        }
      ]
    };
  }

  roiChartInit(): void {
    if (!this.plantDetails?.roi?.graph) {
      this.roiChart = null;
      return;
    }

    const graph = this.plantDetails.roi.graph;

    this.roiChart = {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        height: 284,
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: [''], // Dummy, not shown
        crosshair: true,
        title: {
          text: null
        },
        labels: {
          enabled: false // Hide X-axis labels
        }
      },
      yAxis: {
        min: 0,
        title: {
          text: null
        },
        labels: {
          style: {
            color: '#000',
            fontSize: '12px',
            fontWeight: '400'
          }
        }
      },
      tooltip: {
        valueDecimals: 2,
        valueSuffix: ' kWh',
        shared: true
      },
      legend: {
        enabled: true,
        itemStyle: {
          color: '#000',
          fontSize: '12px',
          fontWeight: '400'
        }
      },
      plotOptions: {
        column: {
          borderRadius: 2,
          pointWidth: 36,
          grouping: true,
          pointPadding: 0.2,
        }
      },
      series: [
        {
          name: 'Total Savings',
          type: 'column',
          data: [graph.TotalSavings_LifetimeEnergy],
          color: '#F9C62D'
        },
        {
          name: 'Investment Recovery',
          type: 'column',
          data: [graph.InvestmentRecovery_InstallationCost],
          color: '#EF4323'
        },
        {
          name: 'Savings Beyond Investment',
          type: 'column',
          data: [graph.SavingsBeyondInvestment],
          color: '#0A9160'
        }
      ]
    };
  }

  openEditPopUp(item: RegionAndCity, contentupdate: any) {
    this.selectedId = item.id;
    this.modalService.open(contentupdate);
  }

  openDownloadPopup(content: any) {
    const stationCode = this.latestQueryParams['stationCode'] || null;
    const date = this.latestQueryParams['date'] || null;
    if (stationCode && date) {
      this.modalRef = this.modalService.open(content, { centered: true });
    } else {
      this.toaster.warning(
        'Please select both Station Code and Date before downloading.');
    }
  }


  private updateQueryParams(paramsToUpdate: any): void {
    this.latestQueryParams = {
      ...this.latestQueryParams,
      ...paramsToUpdate
    };

    if (this.latestQueryParams.search === '') {
      this.latestQueryParams.search = null;
    }
    if (this.latestQueryParams.page === 1) {
      this.latestQueryParams.page = null;
    }

    if (this.latestQueryParams.size === 10) {
      this.latestQueryParams.size = null;
    }
    if (this.latestQueryParams.size === 10) {
      this.latestQueryParams.size = null;
    }
    if (this.latestQueryParams.sort === null) {
      delete this.latestQueryParams.sort;
    }

    if (this.latestQueryParams.column === null) {
      delete this.latestQueryParams.column;
    }
    if (this.latestQueryParams.stationCode === null) {
      delete this.latestQueryParams.stationCode;
    }
    if (this.latestQueryParams.date === null) {
      delete this.latestQueryParams.date;
    }

    if (this.latestQueryParams.page === 1 || (this.latestQueryParams.page && (this.latestQueryParams.search))) {
      if (this.latestQueryParams.page > 1) {
        this.latestQueryParams.page = paramsToUpdate.page;
        if (this.latestQueryParams.size > 10) {
          this.latestQueryParams.size = paramsToUpdate.size;
        }
      } else {
        this.latestQueryParams.page = null;
      }
    }
    if (!this.next_page && (paramsToUpdate.size || paramsToUpdate.search)) {
      // Only include size or search in the query params
      this.latestQueryParams = {
        size: paramsToUpdate.size || null,
        search: paramsToUpdate.search || null
      };
    }
    this.getTableData();
  }
  closeModal() {
    if (this.modalRef) {
      this.modalRef.close('Closed');
    }
  }

  downloadReport(): void {
    const stationCode = this.latestQueryParams['stationCode'] || null;
    const date = this.latestQueryParams['date'] || null;
    const tax = parseFloat(this.form.tax);
    const loss = parseFloat(this.form.lossOfEnergy);
    const remarks = this.form.remarks;

    const isFormValid = !isNaN(tax) && !isNaN(loss);
    const { year, month } = this.getYearMonthFromDate(date);

    if (stationCode && date && isFormValid) {
      this.isDownloading = true;  // start loader
      const payload = {
        station_code: stationCode,
        date: date,
        tax_rate: tax,
        loss_kwh: loss,
        remarks: remarks,
        year: year,
        month: month
      };
      this._utilityService
        .downloadMonthlyReport(payload)
        .subscribe({
          next: (res: Blob) => {
            const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `Monthly_Report_${stationCode}_${date}.xlsx`;
            a.click();

            window.URL.revokeObjectURL(url);
            this.isDownloading = false; // stop loader
            this.closeModal();
            this.resetForm();
          },
          error: () => {
            // this.resetForm();
            this.isDownloading = false; // stop loader on error
            this.toaster.error('Failed to download report. Please try again.');
          }
        });
    } else {
      this.toaster.warning(
        'Please select Plant, Date, and required fields.', '', { timeOut: 5000 });
    }
  }
  // private getTableData(): void {
  //   const stationCode = this.latestQueryParams['stationCode'] || null;
  //   const date = this.latestQueryParams['date'] || null;
  //   if (stationCode && date) {
  //     this.getMonthlyReport(stationCode, date);
  //   }
  //   else {
  //     this.plantDetails = null;
  //   }
  // }
  public getTableData(): void {
    const stationCode = this.latestQueryParams['stationCode'] || null;
    const date = this.latestQueryParams['date'] || null;

    const tax = parseFloat(this.form.tax);
    const loss = parseFloat(this.form.lossOfEnergy);
    const remarks = this.form.remarks;

    const isFormValid = !isNaN(tax) && !isNaN(loss);

    if (isFormValid) {
      if (stationCode && date) {
        this.getMonthlyReport(stationCode, date, tax, loss, remarks);
      }
      else {
        this.plantDetails = null;
        this.toaster.error('Missing required form fields or filters');
      }
    }
    // if (stationCode && date && isFormValid) {
    //   this.getMonthlyReport(stationCode, date, tax, loss, remarks);
    // } else {
    //   this.plantDetails = null;
    //   // Optionally show error
    //   this.toaster.error('Missing required form fields or filters');
    // }
  }
  private getYearMonthFromDate(date: string | null): { year: number | null, month: number | null } {
    if (!date) {
      return { year: null, month: null };
    }

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return { year: null, month: null };
    }

    return {
      year: parsedDate.getFullYear(),
      month: parsedDate.getMonth() + 1 // Month is 0-based
    };
  }

  getMonthlyReport(stationCode: string | null, date: string | null, tax: number | null, loss: number | null, remarks: string | null): void {
    this.isLoadingTableInner = true;
    this.plantDetails = null;
    const { year, month } = this.getYearMonthFromDate(date);

    const payload = {
      station_code: stationCode,
      date: date,
      tax_rate: tax,
      loss_kwh: loss,
      remarks: remarks,
      year: year,
      month: month
    };
    this._utilityService.fetchMonthlyReport(payload).subscribe({
      next: (res: MonthlyReport) => {
        this.plantDetails = res;
        this.isLoadingTableInner = false; // Stop loader after success
        this.billingChartInit(); // ← Add this line
        this.roiChartInit()
      },
      error: (err: any) => {
        console.error('Error fetching daily report:', err);
        this.isLoadingTableInner = false; // Stop loader on error too
        // Optional: Show a toast/snackbar or set a message for UI
      }
    });
  }


  public selectPlants(plants: any): void {
    if (plants) {
      this.updateQueryParams({ stationCode: plants.stationCode });
    } else {
      // If we're resetting, remove stationCode instead of sending null
      this.updateQueryParams({ stationCode: null });
    }
  }
  getPlantsDropDown() {
    this._utilityService.getStationsDropDown().subscribe((res) => {
      if (res) {
        this.allPlantList = res.data;
      }
    });
  }
  onRangeChange(date: Date): void {
    if (date) {
      this.selectedDate = date; // Store the selected month only
      const formattedFromDate = moment(date).format('YYYY-MM-DD');
      this.updateQueryParams({ date: formattedFromDate, });
    } else {
      this.updateQueryParams({ date: null, });
    }
  }

  clearDateRange(): void {
    if (this.selectedDate) {
      this.selectedDate = null
      this.updateQueryParams({ date: null })
    }
  }
  //   initSubscribeForm(): void {
  //   this.subscribeForm = this.formBuilders.group({
  //     plant: [null],
  //     frequency: ['MONTHLY'],
  //     from_day: [null]
  //   });

  //   this.subscribeForm.get('frequency').valueChanges.subscribe((val: string) => {
  //     if (val === 'MONTHLY') {
  //       this.subscribeForm.get('from_day').setValue(null);
  //       this.toDayValue = null;
  //     }
  //   });

  //   this.subscribeForm.get('from_day').valueChanges.subscribe((fromDay: number) => {
  //     if (fromDay) {
  //       this.toDayValue = fromDay + 29 > 31 ? 31 : fromDay + 29;
  //     } else {
  //       this.toDayValue = null;
  //     }
  //   });
  // }
  initSubscribeForm(): void {
    this.subscribeForm = this.formBuilders.group({
      plant: [null],
      frequency: ['MONTHLY'],
      from_day: [null]
    });

    this.subscribeForm.get('frequency').valueChanges.subscribe((val: string) => {
      if (val === 'MONTHLY') {
        this.subscribeForm.get('from_day').setValue(null);
        this.toDayValue = null;
      }
    });

    // this.subscribeForm.get('from_day').valueChanges.subscribe((fromDay: number) => {
    //   if (fromDay) {
    //     this.toDayValue = fromDay + 29 > 31 ? 31 : fromDay + 29;
    //   } else {
    //     this.toDayValue = null;
    //   }
    // });
    this.subscribeForm.get('from_day').valueChanges.subscribe((fromDay: number) => {
      if (fromDay) {
        this.toDayValue = this.calculateToDay(fromDay);
      } else {
        this.toDayValue = null;
      }
    });
  }
  // calculateToDay(fromDay: number): number {
  //   // To day = from day + 29 (30-day range)
  //   const rawToDay = fromDay + 29;

  //   if (rawToDay <= 31) {
  //     return rawToDay;
  //   }

  //   // Overflow: wrap into next month, return last valid day of current month
  //   // Use current month as reference to find last day
  //   const now = new Date();
  //   const lastDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  //   return Math.min(rawToDay, lastDayOfCurrentMonth);
  // }
  calculateToDay(fromDay: number): number {
  const rawToDay = fromDay + 29;

  if (rawToDay <= 31) {
    // stays within same month
    return rawToDay;
  }

  // wraps into next month: e.g. 25 + 29 = 54, 54 - 31 = 23
  return rawToDay - 31;
}

  generateFromDayOptions(): void {
    this.fromDayOptions = Array.from({ length: 31 }, (_, i) => ({
      label: `${i + 1}`,
      value: i + 1
    }));
  }

  openSubscribeModal(content: any): void {
    this.showUnsubscribeConfirm = false;
    this.isLoadingSubscription = true;
    this.modalRef = this.modalService.open(content, { centered: true, size: 'md' });
    this._utilityService.fetchStationInfo(null, null).subscribe((res) => {
      if (res) {
        this.ownedPlants = res.data || [];
        this.updateAvailablePlants(); 
        this.getMonthlySubscription();
      }
    });
  }
  // getMonthlySubscription(): void {
  //   this._utilityService.getSubscription(this.currentUserId, 'MONTHLY').subscribe({
  //     next: (res: any) => {
  //       this.existingSubscription = res || null;

  //       if (res) {
  //         this.subscribedPlants = (res.plants || [])
  //           .map((id: number) => this.ownedPlants.find(p => p.id === id))
  //           .filter(Boolean);

  //         if (res.from_day) {
  //           const now = new Date();
  //           this.fromDate = new Date(now.getFullYear(), now.getMonth(), res.from_day);
  //           this.toDate = new Date(now.getFullYear(), now.getMonth(), res.to_day);
  //           this.subscribeForm.get('frequency').setValue('CUSTOM', { emitEvent: false });
  //         } else {
  //           this.fromDate = null;
  //           this.toDate = null;
  //           this.subscribeForm.get('frequency').setValue('MONTHLY', { emitEvent: false });
  //         }
  //       } else {
  //         this.subscribedPlants = [];
  //         this.fromDate = null;
  //         this.toDate = null;
  //         this.subscribeForm.get('frequency').setValue('MONTHLY', { emitEvent: false });
  //       }

  //       this.isLoadingSubscription = false;
  //     },
  //     error: () => {
  //       this.existingSubscription = null;
  //       this.subscribedPlants = [];
  //       this.isLoadingSubscription = false;
  //     }
  //   });
  // }
  getMonthlySubscription(): void {
    this._utilityService.getSubscription(this.currentUserId, 'MONTHLY').subscribe({
      next: (res: any) => {
        this.existingSubscription = res || null;

        if (res) {
          this.subscribedPlants = (res.plants || [])
            .map((id: number) => this.ownedPlants.find(p => p.id === id))
            .filter(Boolean);
this.updateAvailablePlants(); 
          if (res.from_day) {
            this.subscribeForm.get('frequency').setValue('CUSTOM', { emitEvent: false });
            this.subscribeForm.get('from_day').setValue(res.from_day, { emitEvent: false });
            this.toDayValue = this.calculateToDay(res.from_day); // recalculate, don't trust stored value
          } else {
            this.subscribeForm.get('frequency').setValue('MONTHLY', { emitEvent: false });
            this.subscribeForm.get('from_day').setValue(null, { emitEvent: false });
            this.toDayValue = null;
          }
        } else {
          this.subscribedPlants = [];
          this.updateAvailablePlants(); 
          this.subscribeForm.get('frequency').setValue('MONTHLY', { emitEvent: false });
          this.subscribeForm.get('from_day').setValue(null, { emitEvent: false });
          this.toDayValue = null;
        }

        this.isLoadingSubscription = false;
      },
      error: () => {
        this.existingSubscription = null;
        this.subscribedPlants = [];
        this.updateAvailablePlants(); 
        this.isLoadingSubscription = false;
      }
    });
  }
  // getMonthlySubscription(): void {
  //   this._utilityService.getSubscription(this.currentUserId).subscribe({
  //     next: (res: any) => {
  //       // filter for MONTHLY type
  //       const monthly = Array.isArray(res)
  //         ? res.find((s: any) => s.report_type === 'MONTHLY')
  //         : res?.report_type === 'MONTHLY' ? res : null;

  //       this.existingSubscription = monthly || null;

  //       if (monthly) {
  //         this.subscribedPlants = (monthly.plants || [])
  //           .map((id: number) => this.ownedPlants.find(p => p.id === id))
  //           .filter(Boolean);

  //         const isCustom = monthly.from_day != null;
  //         this.subscribeForm.patchValue({
  //           frequency: isCustom ? 'CUSTOM' : 'MONTHLY',
  //           from_day: monthly.from_day || null
  //         });
  //         if (monthly.from_day) {
  //           this.toDayValue = monthly.to_day;
  //         }
  //       } else {
  //         this.subscribedPlants = [];
  //         this.subscribeForm.reset({ frequency: 'MONTHLY', plant: null, from_day: null });
  //         this.toDayValue = null;
  //       }
  //       this.isLoadingSubscription = false;
  //     },
  //     error: () => {
  //       this.existingSubscription = null;
  //       this.subscribedPlants = [];
  //       this.isLoadingSubscription = false;
  //     }
  //   });
  // }

  onPlantSelect(plant: any): void {
    if (!plant) return;
    if (!this.subscribedPlants.find(p => p.id === plant.id)) {
      this.subscribedPlants.push(plant);
      this.updateAvailablePlants(); 
    }
    this.subscribeForm.get('plant').setValue(null);
  }

  getAvailablePlants(): any[] {
    return (this.ownedPlants || []).filter(p => !this.subscribedPlants.find(sp => sp.id === p.id));
  }
  updateAvailablePlants(): void {
  this.availablePlants = (this.ownedPlants || [])
    .filter(p => !this.subscribedPlants.find(sp => sp.id === p.id));
}
removePlant(plant: any): void {
  const wasFromBackend = this.existingSubscription?.plants?.includes(plant.id);
  this.subscribedPlants = this.subscribedPlants.filter(p => p.id !== plant.id);
  this.updateAvailablePlants();

  if (wasFromBackend) {
    const plantIds = this.subscribedPlants.map(p => p.id);

    // If no plants left, treat as unsubscribed
    if (plantIds.length === 0) {
      const body = { user: this.currentUserId, report_type: 'MONTHLY' };
      this._utilityService.deleteSubscription(body).subscribe({
        next: () => {
          this.existingSubscription = null;
          this.toaster.success(`${plant.stationName} removed`);
        },
        error: () => {
          this.subscribedPlants.push(plant);
          this.updateAvailablePlants();
          this.toaster.error('Failed to remove plant');
        }
      });
      return;
    }

    const body = this.buildSubscriptionBody(plantIds);
    this._utilityService.updateSubscription(body).subscribe({
      next: (res) => {
        this.existingSubscription = res;
        this.toaster.success(`${plant.stationName} removed`);
      },
      error: () => {
        this.subscribedPlants.push(plant);
        this.updateAvailablePlants();
        this.toaster.error('Failed to remove plant');
      }
    });
  }
}
  // removePlant(plant: any): void {
  //   const wasFromBackend = this.existingSubscription?.plants?.includes(plant.id);
  //   this.subscribedPlants = this.subscribedPlants.filter(p => p.id !== plant.id);
  //   this.updateAvailablePlants(); 

  //   if (wasFromBackend) {
  //     const plantIds = this.subscribedPlants.map(p => p.id);
  //     const body = this.buildSubscriptionBody(plantIds);
  //     this._utilityService.updateSubscription(body).subscribe({
  //       next: (res) => {
  //         this.existingSubscription = res;
  //         this.toaster.success(`${plant.stationName} removed`);
  //       },
  //       error: () => {
  //         this.subscribedPlants.push(plant);
  //         this.updateAvailablePlants(); 
  //         this.toaster.error('Failed to remove plant');
  //       }
  //     });
  //   }
  // }

  // buildSubscriptionBody(plantIds: number[]): any {
  //   const frequency = this.subscribeForm.get('frequency').value;
  //   const fromDay = this.subscribeForm.get('from_day').value;
  //   const body: any = {
  //     user: this.currentUserId,
  //     plants: plantIds,
  //     report_type: 'MONTHLY'
  //   };
  //   if (frequency === 'CUSTOM' && fromDay) {
  //     body.from_day = fromDay;
  //     body.to_day = this.toDayValue;
  //   } else {
  //     body.from_day = null;
  //     body.to_day = null;
  //   }
  //   return body;
  // }
  buildSubscriptionBody(plantIds: number[]): any {
  const frequency = this.subscribeForm.get('frequency').value;
  const fromDay = this.subscribeForm.get('from_day').value;
  const body: any = {
    user: this.currentUserId,
    plants: plantIds,
    report_type: 'MONTHLY'
  };
  if (frequency === 'CUSTOM' && fromDay) {
    body.from_day = fromDay;
    body.to_day = this.toDayValue; // calculated via calculateToDay()
  } else {
    body.from_day = null;
    body.to_day = null;
  }
  return body;
}
get nextDeliveryDay(): string | null {
  if (!this.toDayValue) return null;
  
  const fromDay = this.subscribeForm.get('from_day')?.value;
  if (!fromDay) return null;

  const rawToDay = fromDay + 29;
  const isWrapped = rawToDay > 31; // to_day is in next month

  const deliveryDay = this.toDayValue >= 31 ? 1 : this.toDayValue + 1;
  
  // delivery is next month if: to_day was already in next month, OR to_day was 31
  const isNextMonth = isWrapped || this.toDayValue >= 31;

  return isNextMonth ? `${deliveryDay} (next month)` : `${deliveryDay}`;
}

  saveMonthlySubscription(): void {
    const plantIds = this.subscribedPlants.map(p => p.id);
    if (!plantIds.length) {
      this.toaster.warning('Please select at least one plant.');
      return;
    }
    
    const frequency = this.subscribeForm.get('frequency').value;
    if (frequency === 'CUSTOM' && !this.subscribeForm.get('from_day').value) {
      this.toaster.warning('Please select a From Day for custom range.');
      return;
    }
    
    this.isSaving = true;
    const body = this.buildSubscriptionBody(plantIds);
    const request$ = this.existingSubscription
      ? this._utilityService.updateSubscription(body)
      : this._utilityService.createSubscription(body);

    request$
      .pipe(
        finalize(() => {
          this.isSaving = false;
        })
      ).subscribe({
        next: (res) => {
          this.existingSubscription = res;
          this.toaster.success('Subscription saved successfully.');
          this.closeModal();
        },
        error: (err) => {
          this.toaster.error(err?.error?.error || 'Failed to save subscription.');
        }
      });
  }

  confirmUnsubscribe(): void {
    this.showUnsubscribeConfirm = true;
  }

  cancelUnsubscribe(): void {
    this.showUnsubscribeConfirm = false;
  }

  unsubscribe(): void {
    this.isSaving = true;
    const body = { user: this.currentUserId, report_type: 'MONTHLY' };
    this._utilityService.deleteSubscription(body)
      .pipe(
        finalize(() => {
          this.isSaving = false;
        })
      ).subscribe({
        next: () => {
          this.toaster.success('Unsubscribed successfully.');
          this.existingSubscription = null;
          this.subscribedPlants = [];
          this.showUnsubscribeConfirm = false;
          this.closeModal();
        },
        error: () => this.toaster.error('Failed to unsubscribe.')
      });
  }

  resetSubscribeForm(): void {
    this.subscribedPlants = [];
    this.existingSubscription = null;
    this.toDayValue = null;
    this.showUnsubscribeConfirm = false;
    this.updateAvailablePlants(); 
    this.subscribeForm?.reset({ frequency: 'MONTHLY', plant: null, from_day: null });
  }
}
